import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  endTenderApi,
  getequisitionsList,
} from "../../../Services/ReverseAuction/GetApi";
import {
  handleRaFinalizeT_C,
  updateQuantityRequest,
  updateWinnerBidStatus,
} from "../../../Services/ReverseAuction/AlterApi";
import {
  ErrorMessage,
  SuccessMessage,
} from "../../../commons/Toast/ErrorMessage";
import { activeType2Loading } from "../../../Redux/Slices/notificationSlice";
import { activeTenderId, auctionGet } from "../../../Redux/Slices/auctionSlice";

const RESET_UPDATE_QTY = {
  status: false,
  connectId: null,
  minQty: null,
  minBidQuantityMw: null,
  editingQty: null,
  index: null,
};

export const useRASelectionModal = (tenderData, handleClose) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isCloseDatePassed =
    tenderData?.originalTenderCloseDateTime &&
    new Date(tenderData.originalTenderCloseDateTime) < new Date();
  const showSellerMinQty = true;

  const scrollAreaRef = useRef(null);
  const tabRefs = useRef([]);

  const [expanded, setExpanded] = useState(0);
  const [reqList, setReqList] = useState([]);
  const [activeReq, setActiveReq] = useState(null);
  const [editingAlloc, setEditingAlloc] = useState(null);

  // req 5: no pre-selection by default — initialise empty, sync from API on each fetch
  const [selectedBids, setSelectedBids] = useState([]);
  const [rejectedBids, setRejectedBids] = useState([]);
  // track bid IDs absconded in the current session so we don't show stale API "Rejected" on fresh open
  const [sessionAbscondconnectIds, setSessionAbscondconnectIds] = useState(
    new Set(),
  );

  const [confirmation, setConfirmation] = useState({
    status: false,
    header: null,
    data: null,
    note: null,
  });

  const [rejectConfirmation, setRejectConfirmation] = useState({
    status: false,
    header: null,
    data: null,
  });

  const [updateQtyConfirmation, setUpdateQtyConfirmation] =
    useState(RESET_UPDATE_QTY);

  useEffect(() => {
    fetchReqList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const tab = tabRefs.current[expanded];
    const container = scrollAreaRef.current;
    if (!tab || !container) return;
    const tabLeft = tab.offsetLeft;
    const tabWidth = tab.offsetWidth;
    const containerWidth = container.offsetWidth;
    container.scrollTo({
      left: tabLeft - containerWidth / 2 + tabWidth / 2,
      behavior: "smooth",
    });
  }, [expanded]);

  // req 5: no pre-selection — only refresh list data, never overwrite local selection state
  const fetchReqList = async () => {
    const res = await getequisitionsList(tenderData?.id, "AUCTION");
    if (res?.data?.code === 200) {
      const data = res?.data?.data ?? [];
      setReqList(data);
      setActiveReq(data?.[expanded]);
    }
  };

  // ─── Derived state ────────────────────────────────────────────────────────

  const getFilledQty = (ele) =>
    (ele?.bids || [])
      .filter((bid) => selectedBids.includes(bid?.id))
      .reduce(
        (sum, bid) =>
          sum + Number(bid?.requestedQuantity || bid?.selectedQuantity || 0),
        0,
      );

  const hasAnyAccepted = selectedBids.length > 0;
  const allBids = reqList.flatMap((item) => item.bids || []);

  const isBiddingEndPassed =
    tenderData?.bidCloseDateTime &&
    new Date(tenderData.bidCloseDateTime) < new Date();

  const isTenderEndPassed =
    tenderData?.tenderCloseDateTime &&
    new Date(tenderData.tenderCloseDateTime) < new Date();

  const showNoBidState =
    isBiddingEndPassed && !isTenderEndPassed && allBids.length === 0;

  const allReqFirstBidsAbsconded =
    reqList.length > 0 &&
    reqList.every((req) => {
      const sortedBids = [...(req?.bids || [])].sort(
        (a, b) => (a?.bidPrice ?? 0) - (b?.bidPrice ?? 0),
      );
      return sortedBids[0]?.winnerBidStatus === "ABSCOND";
    });

  const allBidsRejected =
    allBids.length > 0 &&
    ((!hasAnyAccepted &&
      rejectedBids.length > 0 &&
      allBids.every((bid) => rejectedBids.includes(bid?.id))) ||
      allBids.every((bid) => bid?.winnerBidStatus === "ABSCOND") ||
      allReqFirstBidsAbsconded);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const onToggle = (index) => {
    setExpanded(index);
    setActiveReq(reqList?.[index]);
  };

  const handleSelectBid = async (id, index) => {
    if (index !== 0) {
      const sortedBids = [...(activeReq?.bids ?? [])].sort(
        (a, b) => (a?.bidPrice ?? 0) - (b?.bidPrice ?? 0),
      );
      const zerothBid = sortedBids[0];
      if (!zerothBid || !selectedBids.includes(zerothBid.id)) {
        ErrorMessage(
          "Please accept the first bid to accept this bid as per tender rules",
        );
        return;
      }
    }
    setSelectedBids((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setRejectedBids((prev) => prev.filter((connectId) => connectId !== id));
    await updateWinnerBidStatus({ connectIdToStatusMap: { [id]: "ACCEPTED" } });
    fetchReqList();
  };

  // req 2: rejecting first bid cancels ALL bids in the requisition
  const handleRejectReqAllBid = async () => {
    const allconnectIds = (activeReq?.bids || []).map((bid) => bid.id);
    const connectIdToStatusMap = allconnectIds.reduce((acc, id) => {
      acc[id] = "ABSCOND";
      return acc;
    }, {});
    setRejectedBids((prev) => [...new Set([...prev, ...allconnectIds])]);
    setSelectedBids((prev) => prev.filter((id) => !allconnectIds.includes(id)));
    await updateWinnerBidStatus({ connectIdToStatusMap });
    fetchReqList();
  };

  // req 2: rejecting any bid cascades rejection to all successive bids
  const handleRejectBid = async (id, index) => {
    if (index === 0) {
      handleRejectReqAllBid();
      return;
    }
    const sortedBids = [...(activeReq?.bids ?? [])].sort(
      (a, b) => (a?.bidPrice ?? 0) - (b?.bidPrice ?? 0),
    );
    const zerothBid = sortedBids[0];
    if (zerothBid) {
      const zerothHasStatus =
        selectedBids.includes(zerothBid.id) ||
        rejectedBids.includes(zerothBid.id);
      if (zerothHasStatus) {
        const bidsToRejectIds = sortedBids.slice(index).map((b) => b.id);
        setRejectedBids((prev) => [...new Set([...prev, ...bidsToRejectIds])]);
        setSelectedBids((prev) =>
          prev.filter((connectId) => !bidsToRejectIds.includes(connectId)),
        );
        await updateWinnerBidStatus({
          connectIdToStatusMap: bidsToRejectIds.reduce((acc, connectId) => {
            acc[connectId] = "ABSCOND";
            return acc;
          }, {}),
        });
      } else {
        ErrorMessage(
          "Please reject the first bid to reject this bid as per tender rules",
        );
        return;
      }
    }
    fetchReqList();
  };

  // req 5: clear only the active requisition's selections
  const handleClear = () => {
    const activeReqconnectIds = (activeReq?.bids || []).map((bid) => bid.id);
    setSelectedBids((prev) =>
      prev.filter((id) => !activeReqconnectIds.includes(id)),
    );
    setRejectedBids((prev) =>
      prev.filter((id) => !activeReqconnectIds.includes(id)),
    );
  };

  const openEditAlloc = (bid) => {
    setEditingAlloc({ connectId: bid?.id, qty: bid?.selectedQuantity || "" });
  };

  const closeEditAlloc = () => setEditingAlloc(null);

  // req 1: qty < minBidQuantityMw → ABSCOND (not PENDING)
  // req 2: first bid blocked at input level; handler guards it too
  // req 4: cascade successive bids when isAbscond
  const handleUpdateQuantityRequest = async (
    connectId,
    minQty,
    index,
    minBidQuantityMw,
  ) => {
    if (Number(editingAlloc?.qty) < 0 || editingAlloc?.qty === "")
      return ErrorMessage("Quantity cannot be negative or empty");
    const qty = Number(editingAlloc?.qty);

    // req 2: first bid must not be saved below seller minimum
    if (
      index === 0 &&
      showSellerMinQty &&
      minBidQuantityMw &&
      qty < Number(minBidQuantityMw)
    )
      return ErrorMessage(
        `First bid allocated quantity cannot be below seller minimum of ${minBidQuantityMw} MW.`,
      );

    // req 1: always ABSCOND when below seller minimum, no PENDING
    const isAbscond =
      showSellerMinQty && minBidQuantityMw && qty < Number(minBidQuantityMw);

    const res = await updateQuantityRequest({
      connectIdToQuantityMap: { [connectId]: qty },
      status: "ACCEPTED",
    });

    if (res?.data?.code === 200) {
      if (isAbscond) {
        // Mark this bid ABSCOND
        await updateWinnerBidStatus({
          connectIdToStatusMap: { [connectId]: "ABSCOND" },
        });

        // req 4: cascade — cancel all successive bids
        const sortedReqBids = [...(activeReq?.bids ?? [])].sort(
          (a, b) => (a?.bidPrice ?? 0) - (b?.bidPrice ?? 0),
        );
        const successiveconnectIds = sortedReqBids
          .slice(index + 1)
          .map((b) => b.id);
        if (successiveconnectIds.length > 0) {
          await updateWinnerBidStatus({
            connectIdToStatusMap: successiveconnectIds.reduce(
              (acc, id) => ({ ...acc, [id]: "ABSCOND" }),
              {},
            ),
          });
        }

        setSessionAbscondconnectIds(
          (prev) => new Set([...prev, connectId, ...successiveconnectIds]),
        );
      }

      SuccessMessage(res?.data?.message);
      closeEditAlloc();
      fetchReqList(); // re-syncs selectedBids/rejectedBids from API
    }
  };

  // req 6: no pending seller approval mention
  const handleModalSubmit = () => {
    setConfirmation({
      status: true,
      header: "Confirm Allocation",
      data: "Once confirmed, documentation will be initiated for the selected bids.",
      note: "An initial obligation will be blocked as per terms and conditions, if applicable.",
    });
  };

  const handleFinalizeT_C = async () => {
    dispatch(activeType2Loading(true));
    const res = await handleRaFinalizeT_C({
      selectedconnectIds: selectedBids,
      tenderId: tenderData?.id,
      bidStage: "AUCTION",
    });
    if (res?.data?.code === 200) {
      SuccessMessage(res?.data?.message);
      localStorage.setItem("activetenderId", tenderData?.id);
      dispatch(activeTenderId(tenderData?.id));
      dispatch(activeType2Loading(false));
      sessionStorage.setItem("lastProductRoute", "/reverse_auction");
      navigate(`/tender_active?id=${tenderData?.capacityId}`);
    } else {
      dispatch(activeType2Loading(false));
      ErrorMessage(res?.data?.message || res?.data?.error);
    }
  };

  const handleRejectAndClose = async () => {
    const res = await endTenderApi(tenderData?.id);
    if (res?.data?.code === 200) {
      SuccessMessage(res?.data?.message);
      dispatch(auctionGet());
      handleClose();
    } else {
      ErrorMessage(res?.data?.message || res?.data?.error);
    }
  };

  const resetUpdateQtyConfirmation = () =>
    setUpdateQtyConfirmation(RESET_UPDATE_QTY);

  return {
    scrollAreaRef,
    tabRefs,
    expanded,
    reqList,
    activeReq,
    editingAlloc,
    setEditingAlloc,
    selectedBids,
    rejectedBids,
    sessionAbscondconnectIds,
    confirmation,
    setConfirmation,
    rejectConfirmation,
    setRejectConfirmation,
    updateQtyConfirmation,
    setUpdateQtyConfirmation,
    isCloseDatePassed,
    showSellerMinQty,
    hasAnyAccepted,
    showNoBidState,
    allBidsRejected,
    getFilledQty,
    onToggle,
    handleSelectBid,
    handleRejectBid,
    handleClear,
    openEditAlloc,
    closeEditAlloc,
    handleUpdateQuantityRequest,
    handleModalSubmit,
    handleFinalizeT_C,
    handleRejectAndClose,
    resetUpdateQtyConfirmation,
  };
};
